import { AddressDto } from "@/app/interfaces/AddressDto";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";

interface AddressFormProps {
  address: AddressDto;
  onChange: (updated: AddressDto) => void;
}

export default function AddressForm({ address, onChange }: AddressFormProps) {
  const update = (field: keyof AddressDto, value: any) => {
    onChange({ ...address, [field]: value });
  };

  const stateValue =
    address.stateProvinceId ??
    (address.availableStates.length > 0
      ? Number(address.availableStates[0].value)
      : "");

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          fontWeight={600}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
          fontSize={"14px"}
          sx={{ mb: 0.5 }}
        >
          Company
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={address.company || ""}
          onChange={(e) => update("company", e.target.value)}
        />
      </Grid>

      {/* FIRST NAME */}
      <Grid item xs={12} sm={6}>
        <Typography
          fontWeight={600}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
          fontSize={"14px"}
          sx={{ mb: 0.5 }}
        >
          First Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={address.firstName || ""}
          onChange={(e) => update("firstName", e.target.value)}
        />
      </Grid>

      {/* LAST NAME */}
      <Grid item xs={12} sm={6}>
        <Typography
          fontWeight={600}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
          fontSize={"14px"}
          sx={{ mb: 0.5 }}
        >
          Last Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={address.lastName || ""}
          onChange={(e) => update("lastName", e.target.value)}
        />
      </Grid>

      {/* ADDRESS 1 */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily={'"Montserrat",Verdana,sans-serif'}
            fontSize={"14px"}
          >
            Address 1
          </Typography>

          <Typography
            component="span"
            sx={{ color: "red", fontSize: "18px", lineHeight: 1 }}
          >
            *
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          size="small"
          value={address.address1 || ""}
          onChange={(e) => update("address1", e.target.value)}
        />
      </Grid>

      {/* ADDRESS 2 */}
      <Grid item xs={12} sm={6}>
        <Typography
          fontWeight={600}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
          fontSize={"14px"}
          sx={{ mb: 0.5 }}
        >
          Address 2
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={address.address2 || ""}
          onChange={(e) => update("address2", e.target.value)}
        />
      </Grid>

      {/* CITY */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily={'"Montserrat",Verdana,sans-serif'}
            fontSize={"14px"}
          >
            City
          </Typography>

          <Typography
            component="span"
            sx={{ color: "red", fontSize: "18px", lineHeight: 1 }}
          >
            *
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          size="small"
          value={address.city || ""}
          onChange={(e) => update("city", e.target.value)}
        />
      </Grid>

      {/* STATE */}
      <Grid item xs={12} sm={6}>
        <Typography
          fontWeight={600}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
          fontSize={"14px"}
          sx={{ mb: 0.5 }}
        >
          State / Province
        </Typography>
        <FormControl fullWidth>
          <Select
            size="small"
            value={stateValue}
            onChange={(e) => update("stateProvinceId", Number(e.target.value))}
          >
            {address.availableStates.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* ZIP */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily={'"Montserrat",Verdana,sans-serif'}
            fontSize={"14px"}
          >
            Zip / Postal Code
          </Typography>

          <Typography
            component="span"
            sx={{ color: "red", fontSize: "18px", lineHeight: 1 }}
          >
            *
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          size="small"
          inputProps={{ maxLength: 5 }}
          value={address.zipPostalCode || ""}
          onChange={(e) => update("zipPostalCode", e.target.value)}
        />
      </Grid>

      {/* PHONE */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily={'"Montserrat",Verdana,sans-serif'}
            fontSize={"14px"}
          >
            Phone Number
          </Typography>

          <Typography
            component="span"
            sx={{ color: "red", fontSize: "18px", lineHeight: 1 }}
          >
            *
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          size="small"
          value={address.phoneNumber || ""}
          onChange={(e) => update("phoneNumber", e.target.value)}
        />
      </Grid>
    </Grid>
  );
}
